<template>
    <div 
        class="bottom-sheet" 
        :class="{ 'expanded': isExpanded, 'dragging': isDragging }"
        :style="dragStyles"
        @touchstart="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd"
    >
        <div class="drag-handle" @click="toggleExpanded">
            <div class="drag-indicator"></div>
        </div>
        
        <!-- Error State -->
        <div v-if="error" class="error-state">
            <div class="error-icon">⚠️</div>
            <p>{{ error }}</p>
            <button @click="$emit('retry')" class="retry-button">Spróbuj ponownie</button>
        </div>
        
        <!-- Runner Header Banner -->
        <div v-else-if="runner" class="runner-header">
            <div class="runner-name">
                <span class="name">{{ runner.firstName }} {{ runner.lastName }}</span>
                <span class="number">{{ runner.number }}</span>
            </div>
            <div class="runner-stats">
                <div class="stat">
                    <span class="stat-label">Czas:</span>
                    <span class="stat-value">{{ runner.totalTime }}</span>
                </div>
                <div class="stat">
                    <span class="stat-label">Start:</span>
                    <span class="stat-value">{{ runner.formatedStartTime }}</span>
                </div>
                <div class="stat">
                    <span class="stat-label">Dystans:</span>
                    <span class="stat-value">{{ runner.distance }}</span>
                </div>
                <div class="stat">
                    <span class="stat-label">śr. prędkość:</span>
                    <span class="stat-value">{{ runner.averageSpeed }}</span>
                </div>
            </div>
            <div class="runner-stats runner-stats-secondary">
                <div class="stat">
                    <span class="stat-label">ostatni punkt:</span>
                    <span class="stat-value">{{ runner.lastCheckpoint }}</span>
                </div>
                <div class="stat">
                    <span class="stat-label">następny punkt:</span>
                    <span class="stat-value">{{ runner.nextCheckpoint }}</span>
                </div>
                <div class="stat">
                    <span class="stat-label">szacowana godzina ukończenia:</span>
                    <span class="stat-value">{{ runner.estimatedFinishTime }}</span>
                </div>
                <div class="stat">
                    <span class="stat-label">czas ostatniego śladu:</span>
                    <span class="stat-value">{{ runner.lastTrackTime }}</span>
                </div>
            </div>
            <div class="runner-stats runner-stats-tertiary">
                <div class="stat">
                    <span class="stat-label">aktualna prędkość:</span>
                    <span class="stat-value">{{ runner.currentSpeed }}</span>
                </div>
                <div class="stat">
                    <span class="stat-label">wysokość n.p.m.:</span>
                    <span class="stat-value">{{ runner.currentAltitude }}</span>
                </div>
                <div class="stat">
                    <span class="stat-label">Przewyższenie:</span>
                    <span class="stat-value">{{ runner.elevationGain }}</span>
                </div>
                <div class="stat">
                    <span class="stat-label">stan baterii GPS:</span>
                    <span class="stat-value">{{ runner.gpsBattery }}</span>
                </div>
            </div>
            <div class="runner-info">
                {{ runner.eventInfo }}
            </div>
        </div>

        <!-- Expanded Content -->
        <div v-if="shouldShowExpandedContent" class="expanded-content" :style="expandedContentStyles">
            <!-- Checkpoints Section -->
            <div class="checkpoints-section">
                <h3>Punkty kontrolne</h3>
                <div class="checkpoints-table">
                    <div class="table-header">
                        <span class="col-distance">Km</span>
                        <span class="col-name">Nazwa</span>
                        <span class="col-time">Czas</span>
                        <span class="col-split">Odcinek</span>
                    </div>
                    <div v-for="checkpoint in runner.checkpoints" :key="checkpoint.id" class="table-row">
                        <span class="col-distance">{{ checkpoint.distance }}</span>
                        <span class="col-name">{{ checkpoint.name }}</span>
                        <span class="col-time">{{ checkpoint.time }}</span>
                        <span class="col-split">{{ checkpoint.splitTime }}</span>
                    </div>
                </div>
            </div>

            <!-- Elevation Chart Section -->
            <div v-if="hasElevationData" class="elevation-section">
                <h3>Wykres wysokości</h3>
                <ElevationChart 
                    :route="eventRouteWithMarkers.route"
                    :markers="eventRouteWithMarkers.markers"
                    :userPosition="runner.latestPosition"
                    :checkpoints="runner.checkpoints"
                    :width="360"
                    :height="180"
                />
            </div>
        </div>

        <!-- Desktop-only expanded content (always visible on desktop) -->
        <div v-if="runner" class="expanded-content desktop-only-expanded">
            <!-- Checkpoints Section -->
            <div class="checkpoints-section">
                <h3>Punkty kontrolne</h3>
                <div class="checkpoints-table">
                    <div class="table-header">
                        <span class="col-distance">Km</span>
                        <span class="col-name">Nazwa</span>
                        <span class="col-time">Czas</span>
                        <span class="col-split">Odcinek</span>
                    </div>
                    <div v-for="checkpoint in runner.checkpoints" :key="checkpoint.id" class="table-row">
                        <span class="col-distance">{{ checkpoint.distance }}</span>
                        <span class="col-name">{{ checkpoint.name }}</span>
                        <span class="col-time">{{ checkpoint.time }}</span>
                        <span class="col-split">{{ checkpoint.splitTime }}</span>
                    </div>
                </div>
            </div>

            <!-- Elevation Chart Section -->
            <div v-if="hasElevationData" class="elevation-section">
                <h3>Wykres wysokości</h3>
                <ElevationChart 
                    :route="eventRouteWithMarkers.route"
                    :markers="eventRouteWithMarkers.markers"
                    :userPosition="runner.latestPosition"
                    :checkpoints="runner.checkpoints"
                    :width="400"
                    :height="200"
                />
            </div>
        </div>
    </div>
</template>

<script>
import ElevationChart from './ElevationChart.vue'
import { hasElevationData } from '../services/api.js'

export default {
    name: 'BottomSheet',
    components: {
        ElevationChart
    },
    props: {
        runnerData: {
            type: Object,
            default: null
        },
        eventRoute: {
            type: Array,
            default: () => []
        },
        eventMarkers: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            isExpanded: false,
            startY: 0,
            currentY: 0,
            isDragging: false,
            dragOffset: 0,
            initialHeight: 120, // collapsed height
            maxHeight: 0, // will be set to window height
            startTime: 0,
            velocity: 0
        }
    },
    computed: {
        runner() {
            return this.runnerData
        },
        eventRouteWithMarkers() {
            return {
                route: this.eventRoute,
                markers: this.eventMarkers
            }
        },
        dragStyles() {
            if (!this.isDragging) {
                return {}
            }
            return {
                transform: `translateY(${this.dragOffset}px)`,
                transition: 'none'
            }
        },
        // Calculate progress of expansion during drag (0 = collapsed, 1 = expanded)
        dragProgress() {
            if (!this.isDragging) {
                return this.isExpanded ? 1 : 0
            }
            
            // When collapsed and dragging up (negative offset), calculate progress
            if (!this.isExpanded && this.dragOffset < 0) {
                const progress = Math.abs(this.dragOffset) / 100 // 100px for full reveal
                return Math.min(progress, 1)
            }
            
            // When expanded and dragging down (positive offset), calculate reverse progress
            if (this.isExpanded && this.dragOffset > 0) {
                const progress = 1 - (this.dragOffset / 100)
                return Math.max(progress, 0)
            }
            
            return this.isExpanded ? 1 : 0
        },
        // Determine if expanded content should be visible
        shouldShowExpandedContent() {
            return this.isExpanded || (this.isDragging && this.dragProgress > 0)
        },
        // Calculate styles for expanded content during drag
        expandedContentStyles() {
            const opacity = this.dragProgress
            const translateY = (1 - this.dragProgress) * 20 // Slide up effect
            
            return {
                opacity: opacity,
                transform: `translateY(${translateY}px)`,
                transition: this.isDragging ? 'none' : 'opacity 0.3s ease, transform 0.3s ease'
            }
        },
        // Check if elevation data is available for charts
        hasElevationData() {
            return this.runner && this.runner.tracks && hasElevationData(this.runner.tracks)
        }
    },
    mounted() {
        this.maxHeight = window.innerHeight
        window.addEventListener('resize', this.updateMaxHeight)
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.updateMaxHeight)
    },
    methods: {
        updateMaxHeight() {
            this.maxHeight = window.innerHeight
        },
        toggleExpanded() {
            this.isExpanded = !this.isExpanded
        },
        isDesktopView() {
            return window.innerWidth >= 1024
        },
        onTouchStart(e) {
            // Disable touch events on desktop
            if (this.isDesktopView()) return
            
            this.startY = e.touches[0].clientY
            this.currentY = this.startY
            this.isDragging = true
            this.dragOffset = 0
            this.startTime = Date.now()
            this.velocity = 0
            
            // Prevent default to avoid conflicts with browser gestures
            e.preventDefault()
        },
        onTouchMove(e) {
            // Disable touch events on desktop
            if (!this.isDragging || this.isDesktopView()) return
            
            this.currentY = e.touches[0].clientY
            const deltaY = this.currentY - this.startY
            
            // Calculate velocity for momentum
            const currentTime = Date.now()
            const timeDelta = currentTime - this.startTime
            if (timeDelta > 0) {
                this.velocity = deltaY / timeDelta
            }
            
            // Apply resistance and boundaries
            let newOffset = deltaY
            
            if (this.isExpanded) {
                // When expanded, only allow downward movement (positive deltaY)
                newOffset = Math.max(0, deltaY)
                // Add resistance when dragging too far
                if (newOffset > 100) {
                    newOffset = 100 + (newOffset - 100) * 0.3
                }
            } else {
                // When collapsed, only allow upward movement (negative deltaY)
                newOffset = Math.min(0, deltaY)
                // Add resistance when dragging too far
                if (newOffset < -100) {
                    newOffset = -100 + (newOffset + 100) * 0.3
                }
            }
            
            this.dragOffset = newOffset
            
            // Prevent default to avoid scroll and refresh
            e.preventDefault()
        },
        onTouchEnd(e) {
            // Disable touch events on desktop
            if (!this.isDragging || this.isDesktopView()) return
            
            const deltaY = this.currentY - this.startY
            const threshold = 50 // minimum swipe distance
            const velocityThreshold = 0.3 // minimum velocity for momentum-based decision
            
            // Decide whether to expand/collapse based on distance and velocity
            let shouldToggle = false
            
            if (Math.abs(this.velocity) > velocityThreshold) {
                // Use velocity for decision
                if (this.velocity > 0 && this.isExpanded) {
                    // Fast downward swipe when expanded - collapse
                    shouldToggle = true
                } else if (this.velocity < 0 && !this.isExpanded) {
                    // Fast upward swipe when collapsed - expand
                    shouldToggle = true
                }
            } else {
                // Use distance for decision
                if (deltaY > threshold && this.isExpanded) {
                    // Swipe down when expanded - collapse
                    shouldToggle = true
                } else if (deltaY < -threshold && !this.isExpanded) {
                    // Swipe up when collapsed - expand
                    shouldToggle = true
                }
            }
            
            if (shouldToggle) {
                this.isExpanded = !this.isExpanded
            }
            
            // Reset dragging state
            this.isDragging = false
            this.dragOffset = 0
            this.velocity = 0
            
            // Prevent default to avoid any click events from firing
            e.preventDefault()
        }
    }
}
</script>

<style scoped>
.bottom-sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    background: #fff;
    box-shadow: 0 -2px 8px rgba(0,0,0,0.15);
    z-index: 1000;
    padding: 0;
    height: auto;
    min-height: 120px;
    transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    transform: translateY(0);
    touch-action: none; /* Disable browser touch gestures */
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
}

.bottom-sheet.expanded {
    height: 100vh;
    height: 100dvh; /* Dynamic viewport height for mobile */
}

.bottom-sheet.dragging {
    transition: none; /* Disable transitions during dragging for immediate response */
}

.drag-handle {
    padding: 8px 12px 4px 12px;
    text-align: center;
    cursor: pointer;
    user-select: none;
    background: transparent;
    touch-action: none; /* Ensure touch events are handled by our code */
}

.drag-indicator {
    width: 40px;
    height: 4px;
    background: #2c3e50;
    border-radius: 2px;
    margin: 0 auto;
    transition: background-color 0.2s ease;
}

.drag-handle:hover .drag-indicator {
    background: #34495e;
}

/* Runner Header Banner */
.runner-header {
    background: #2c3e50;
    color: white;
    padding: 16px;
    margin: 0;
}

.runner-name {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-bottom: 12px;
    gap: 12px;
}

.runner-name .name {
    font-size: 18px;
    font-weight: bold;
}

.runner-name .number {
    background: #f1c40f;
    color: #2c3e50;
    padding: 4px 8px;
    border-radius: 4px;
    font-weight: bold;
    font-size: 14px;
}

.runner-stats {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 16px;
    font-weight: 600;
    gap: 8px;
}

.runner-stats-secondary {
    margin-bottom: 8px;
}

.runner-stats-tertiary {
    margin-bottom: 8px;
    font-size: 14px;
}

.runner-stats .stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    flex: 1;
}

.stat-label {
    font-size: 11px;
    font-weight: 400;
    opacity: 0.8;
    margin-bottom: 2px;
}

.stat-value {
    font-size: 14px;
    font-weight: 600;
}

.runner-info {
    font-size: 12px;
    opacity: 0.8;
}

/* Expanded Content */
.expanded-content {
    padding: 20px 16px;
    height: calc(100% - 120px);
    overflow-y: auto;
}

.checkpoints-section {
    margin-bottom: 32px;
}

.checkpoints-section h3 {
    font-size: 16px;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 16px;
    margin-top: 0;
}

.checkpoints-table {
    border: 1px solid #e9ecef;
    border-radius: 6px;
    overflow: hidden;
}

.table-header {
    display: grid;
    grid-template-columns: 40px 1fr 80px 80px;
    background: #f8f9fa;
    padding: 12px 8px;
    font-weight: 600;
    font-size: 12px;
    color: #495057;
    border-bottom: 1px solid #e9ecef;
}

.table-row {
    display: grid;
    grid-template-columns: 40px 1fr 80px 80px;
    padding: 12px 8px;
    border-bottom: 1px solid #f1f3f4;
    font-size: 14px;
}

.table-row:last-child {
    border-bottom: none;
}

.col-distance {
    text-align: center;
    font-weight: 600;
    color: #0066cc;
    font-size: 12px;
}

.col-name {
    text-align: left;
    padding-left: 8px;
}

.col-time {
    text-align: center;
    font-size: 12px;
}

.col-split {
    text-align: center;
    font-size: 12px;
    font-weight: 600;
}

.elevation-section h3 {
    font-size: 16px;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 16px;
    margin-top: 0;
}

.elevation-placeholder {
    background: #f8f9fa;
    border: 2px dashed #dee2e6;
    border-radius: 8px;
    padding: 40px 20px;
    text-align: center;
    color: #6c757d;
    font-style: italic;
}

/* Desktop/Laptop optimizations */
@media (min-width: 1024px) {
    .bottom-sheet {
        position: fixed;
        left: auto;
        right: 0;
        bottom: 0;
        top: 0;
        width: 400px;
        height: 100vh;
        min-height: auto;
        border-radius: 0;
        border-left: 1px solid #e9ecef;
        box-shadow: -2px 0 8px rgba(0,0,0,0.15);
        overflow-y: auto;
    }
    
    .bottom-sheet.expanded {
        height: 100vh; /* Always full height on desktop */
    }
    
    .drag-handle {
        display: none; /* Hide drag handle on desktop */
    }
    
    .runner-header {
        position: sticky;
        top: 0;
        z-index: 1001;
    }
    
    /* Hide the mobile expanded content on desktop */
    .expanded-content:not(.desktop-only-expanded) {
        display: none !important;
    }
    
    /* Show the desktop-only expanded content */
    .desktop-only-expanded {
        display: block !important;
        opacity: 1 !important;
        height: auto !important;
        padding: 20px;
    }
}

/* Hide desktop-only content on mobile/tablet */
@media (max-width: 1023px) {
    .desktop-only-expanded {
        display: none !important;
    }
}

/* Tablet optimizations */
@media (min-width: 769px) and (max-width: 1023px) {
    .bottom-sheet {
        max-height: 50vh;
    }
    
    .bottom-sheet.expanded {
        max-height: 80vh;
    }
}

/* Mobile optimizations */
@media (max-width: 768px) {
    .bottom-sheet {
        min-height: 100px;
    }
    
    .runner-header {
        padding: 12px;
    }
    
    .runner-name .name {
        font-size: 16px;
    }
    
    .runner-stats {
        font-size: 14px;
        gap: 4px;
    }
    
    .stat-label {
        font-size: 10px;
    }
    
    .stat-value {
        font-size: 12px;
    }
    
    .expanded-content {
        padding: 16px 12px;
    }
    
    .table-header,
    .table-row {
        grid-template-columns: 30px 1fr 70px 70px;
        padding: 10px 6px;
    }
    
    .col-time,
    .col-split {
        font-size: 11px;
    }
}

/* Loading and Error States */
.loading-state {
    padding: 40px 20px;
    text-align: center;
    color: #8a94a6;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #f3f4f6;
    border-top: 3px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 20px;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.loading-state p {
    margin: 0;
    font-size: 14px;
}

.error-state {
    padding: 40px 20px;
    text-align: center;
    color: #e74c3c;
}

.error-icon {
    font-size: 48px;
    margin-bottom: 16px;
}

.error-state p {
    margin: 0 0 20px 0;
    font-size: 14px;
    line-height: 1.4;
}

.retry-button {
    background: #3498db;
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.retry-button:hover {
    background: #2980b9;
}

/* Prevent scrolling behind the sheet when expanded */
.bottom-sheet.expanded {
    overflow: hidden;
}

/* Improve performance during animations */
.bottom-sheet {
    will-change: transform, height;
}
</style>